import { supabase } from '../supabase';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExchangeRate } from '@shiftly/shared';
import { DateTime } from 'luxon';

const SupabaseExchangeRateService = {
  async getExchangeRates(): Promise<ExchangeRate[]> {
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('*')
      .order('date', { ascending: false });

    if (error) throw error;

    return data.map(row => ({
      id: row.id,
      rate: row.rate,
      date: DateTime.fromISO(row.date, { zone: 'UTC' }),
      created_at: DateTime.fromISO(row.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(row.updated_at, { zone: 'UTC' }),
    } as ExchangeRate));
  },

  async updateExchangeRate(
    rate: number,
    date: DateTime,
    id: string,
  ): Promise<void> {
    const new_date = date.startOf("month");

    // Update existing rate
    const { error } = await supabase
      .from('exchange_rates')
      .update({
        rate,
        date: new_date.toISO(),
      })
      .eq('id', id);

    if (error) throw error;
  },

  async deleteExchangeRate(id: string): Promise<void> {
    const { error } = await supabase
      .from('exchange_rates')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// Query hook for fetching exchange rates
export const useExchangeRates = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['exchangeRates'],
    queryFn: SupabaseExchangeRateService.getExchangeRates,
    enabled: enabled
  });
};

// Mutation hook for creating/updating exchange rates
export const useUpdateExchangeRate = () => {
  return useMutation({
    mutationFn: (params: { rate: number; date: DateTime; id: string }) =>
      SupabaseExchangeRateService.updateExchangeRate(params.rate, params.date, params.id)
  });
};

// Mutation hook for deleting exchange rates
export const useDeleteExchangeRate = () => {
  return useMutation({
    mutationFn: SupabaseExchangeRateService.deleteExchangeRate
  });
};

export default SupabaseExchangeRateService;